import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/MultiSelect";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck } from "lucide-react";

export default function Attrezzature() {
  const [items, setItems] = useState([]);
  const [activities, setActivities] = useState([]);
  const [name, setName] = useState("");
  const [newActs, setNewActs] = useState([]);

  const load = () => api.get("/equipment").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/activity-types").then((r) => setActivities(r.data)).catch(() => {});
  }, []);

  const actName = (id) => activities.find((a) => a.id === id)?.name || id;

  const add = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/equipment", { name: name.trim(), activity_type_ids: newActs });
      setName("");
      setNewActs([]);
      load();
      toast.success("Aggiunta");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const updateActs = async (it, ids) => {
    try {
      await api.put(`/equipment/${it.id}`, { name: it.name, activity_type_ids: ids });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, activity_type_ids: ids } : x)));
    } catch (e) {
      toast.error("Errore durante l'aggiornamento");
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/equipment/${id}`); load(); } catch (e) { toast.error("Errore"); }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Dispositivi & Attrezzature</h1>
        <p className="mt-1 text-sm text-slate-500">Elenco verificato dall'operatore durante il check-in. Associa ogni voce ai tipi di attività per cui è richiesta.</p>
      </div>
      <Card className="rounded-lg border-slate-200 p-5 shadow-sm">
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Es. Estintore, Kit primo soccorso, DPI..." data-testid="equipment-input" />
          <Button onClick={add} data-testid="equipment-add-btn" className="shrink-0 rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]"><Plus className="mr-1.5 h-4 w-4" />Aggiungi</Button>
        </div>
        {activities.length > 0 && (
          <div className="mt-2">
            <Label className="text-xs text-slate-500">Tipi di attività (facoltativo — vuoto = per tutti)</Label>
            <div className="mt-1.5">
              <MultiSelect
                testid="equipment-new-activities"
                options={activities.map((a) => ({ value: a.id, label: a.name }))}
                value={newActs}
                onChange={setNewActs}
                allLabel="Tutte le attività"
                placeholder="Cerca attività..."
              />
            </div>
          </div>
        )}
        <div className="mt-4 space-y-2">
          {items.length === 0 ? <p className="text-sm text-slate-400">Nessuna attrezzatura configurata.</p> : items.map((it) => (
            <div key={it.id} className="rounded-md border border-slate-100 p-3" data-testid={`equipment-row-${it.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm text-slate-800"><ShieldCheck className="h-4 w-4 text-emerald-600" />{it.name}</span>
                <button onClick={() => remove(it.id)} data-testid={`equipment-del-${it.id}`} className="text-rose-500 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button>
              </div>
              {activities.length > 0 && (
                <div className="mt-2">
                  {(it.activity_type_ids || []).length > 0 && (
                    <div className="mb-1.5 flex flex-wrap gap-1.5" data-testid={`equipment-acts-${it.id}`}>
                      {(it.activity_type_ids || []).map((aid) => (
                        <span key={aid} className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-[#002FA7]">{actName(aid)}</span>
                      ))}
                    </div>
                  )}
                  <MultiSelect
                    testid={`equipment-acts-select-${it.id}`}
                    options={activities.map((a) => ({ value: a.id, label: a.name }))}
                    value={it.activity_type_ids || []}
                    onChange={(ids) => updateActs(it, ids)}
                    allLabel="Tutte le attività"
                    placeholder="Assegna attività..."
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
